<template>
  <!-- eslint-disable vue/camelcase -->
  <div class="container-fluid p-3">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="h3 my-0 mx-0 text-foreground">Settings</h3>
        <p class="text-muted-foreground text-sm">Manage your account settings and preferences</p>
      </div>
    </div>
    <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />

    <div v-if="business" class="settings-content">
      <!-- Tab Navigation -->
      <div class="tabs-container">
        <div class="tabs-list" role="tablist">
          <button
            type="button"
            role="tab"
            :aria-selected="activeTab === 'account'"
            class="tab-button"
            :class="{ active: activeTab === 'account' }"
            @click="activeTab = 'account'"
          >
            Account
          </button>
          <button
            v-if="user && user.business"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'settings'"
            class="tab-button"
            :class="{ active: activeTab === 'settings' }"
            @click="activeTab = 'settings'"
          >
            Settings
          </button>
          <button
            v-if="showConnectedSites"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'connected-sites'"
            class="tab-button"
            :class="{ active: activeTab === 'connected-sites' }"
            @click="activeTab = 'connected-sites'"
          >
            Connected Sites
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeTab === 'ai-prompt'"
            class="tab-button"
            :class="{ active: activeTab === 'ai-prompt' }"
            @click="activeTab = 'ai-prompt'"
          >
            AI Review Reply Prompt
          </button>
          <button
            v-if="user.business && (user.business.is_social_review || user.business.is_social_fusion)"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'history'"
            class="tab-button"
            :class="{ active: activeTab === 'history' }"
            @click="handleHistoryTabClick"
          >
            History
          </button>
          <button
            v-if="user && user.business && user.business.is_text_requester"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'automated-requests'"
            class="tab-button"
            :class="{ active: activeTab === 'automated-requests' }"
            @click="activeTab = 'automated-requests'"
          >
            Automated Review Requests
          </button>
        </div>
      </div>

      <!-- Tab Panels -->
      <div class="tab-panels-wrapper">
        <!-- Account Tab: profile + password -->
        <div
          v-show="activeTab === 'account'"
          class="tab-panel"
        >
          <div class="account-tab-content">
            <div class="settings-section">
              <div class="recipient-card">
                <div class="recipient-header">
                  <h3 class="recipient-title">Profile</h3>
                </div>
                <ValidationObserver v-slot="{ handleSubmit, invalid, touched }" ref="accountProfileForm">
                  <form class="account-profile-form" @submit.prevent="handleSubmit(updateAccountProfile)">
                    <div class="account-form-row">
                      <div class="account-form-group">
                        <label class="account-label">First Name</label>
                        <input
                          v-model="user.first_name"
                          type="text"
                          class="account-input"
                          placeholder="First Name"
                          required
                        />
                      </div>
                      <div class="account-form-group">
                        <label class="account-label">Last Name</label>
                        <input
                          v-model="user.last_name"
                          type="text"
                          class="account-input"
                          placeholder="Last Name"
                          required
                        />
                      </div>
                    </div>
                    <div class="account-form-group">
                      <label class="account-label">Email</label>
                      <input
                        v-model="user.email"
                        type="email"
                        class="account-input"
                        placeholder="Email"
                        required
                      />
                    </div>
                    <div class="account-form-actions">
                      <button
                        type="submit"
                        class="add-button"
                        :disabled="loadingAccountUpdate || (invalid && touched)"
                      >
                        <b-spinner v-if="loadingAccountUpdate" small />
                        <span v-else>Update profile</span>
                      </button>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
            <div class="settings-section">
              <div class="recipient-card">
                <div class="recipient-header">
                  <h3 class="recipient-title">Change password</h3>
                </div>
                <div class="account-password-section">
                  <PasswordReset />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Settings Tab -->
        <div
          v-if="user && user.business"
          v-show="activeTab === 'settings'"
          class="tab-panel"
        >
          <div class="settings-tab-content">
            <!-- Users Section -->
            <div class="settings-section">
              <div class="recipient-card">
                <div class="recipient-header">
                  <div class="recipient-title-group">
                    <h3 class="recipient-title">Users</h3>
                    <span class="recipient-count">{{ usersCount }}</span>
                  </div>
                  <button class="add-button" @click="showAddUserModal">
                    <i class="uil uil-plus"></i>
                    Add User
                  </button>
                </div>
                <div class="recipient-table-wrapper">
                  <table class="recipient-table">
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th class="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(userItem, index) in users" :key="index">
                        <td>{{ userItem.first_name }}</td>
                        <td>{{ userItem.last_name }}</td>
                        <td>{{ userItem.email }}</td>
                        <td class="text-right">
                          <button class="action-btn edit-btn" @click="editUser(userItem)">
                            <i class="uil uil-edit"></i>
                            Edit
                          </button>
                          <button class="action-btn edit-btn" @click="openChangePasswordModal(userItem)">
                            <i class="uil uil-key-skeleton"></i>
                            Change password
                          </button>
                          <button class="action-btn delete-btn" @click="deleteUser(userItem)">
                            <i class="uil uil-trash"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- New Review Notification Recipients -->
            <div class="settings-section">
              <div class="recipient-card">
                <div class="recipient-header">
                  <div class="recipient-title-group">
                    <h3 class="recipient-title">New Review Notification Recipients</h3>
                    <span class="recipient-count">{{ newReviewRecipientsCount }}</span>
                  </div>
                  <button class="add-button" @click="showAddRecipientModal('new_review')">
                    <i class="uil uil-plus"></i>
                    Add Recipient
                  </button>
                </div>
                <div class="recipient-table-wrapper">
                  <table class="recipient-table">
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th class="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(recipient, index) in newReviewRecipients" :key="index">
                        <td>{{ recipient.first_name }}</td>
                        <td>{{ recipient.last_name }}</td>
                        <td>{{ recipient.email }}</td>
                        <td class="text-right">
                          <button class="action-btn edit-btn" @click="editRecipient(recipient, 'new_review')">
                            <i class="uil uil-edit"></i>
                            Edit
                          </button>
                          <button class="action-btn delete-btn" @click="deleteRecipient(recipient, 'new_review')">
                            <i class="uil uil-trash"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Social Media Post Notification Recipients -->
            <div v-if="user.business && user.business.is_social_review" class="settings-section">
              <div class="recipient-card">
                <div class="recipient-header">
                  <div class="recipient-title-group">
                    <h3 class="recipient-title">Social Media Post Notification Recipients</h3>
                    <span class="recipient-count">{{ socialPostRecipientsCount }}</span>
                  </div>
                  <button class="add-button" @click="showAddRecipientModal('social_post')">
                    <i class="uil uil-plus"></i>
                    Add Recipient
                  </button>
                </div>
                <div class="recipient-table-wrapper">
                  <table class="recipient-table">
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th class="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(recipient, index) in socialPostRecipients" :key="index">
                        <td>{{ recipient.first_name }}</td>
                        <td>{{ recipient.last_name }}</td>
                        <td>{{ recipient.email }}</td>
                        <td class="text-right">
                          <button class="action-btn edit-btn" @click="editRecipient(recipient, 'social_post')">
                            <i class="uil uil-edit"></i>
                            Edit
                          </button>
                          <button class="action-btn delete-btn" @click="deleteRecipient(recipient, 'social_post')">
                            <i class="uil uil-trash"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Review Reply Notification Recipients -->
            <div class="settings-section">
              <div class="recipient-card">
                <div class="recipient-header">
                  <div class="recipient-title-group">
                    <h3 class="recipient-title">Review Reply Notification Recipients</h3>
                    <span class="recipient-count">{{ reviewReplyRecipientsCount }}</span>
                  </div>
                  <button class="add-button" @click="showAddRecipientModal('review_reply')">
                    <i class="uil uil-plus"></i>
                    Add Recipient
                  </button>
                </div>
                <div class="recipient-table-wrapper">
                  <table class="recipient-table">
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th class="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(recipient, index) in reviewReplyRecipients" :key="index">
                        <td>{{ recipient.first_name }}</td>
                        <td>{{ recipient.last_name }}</td>
                        <td>{{ recipient.email }}</td>
                        <td class="text-right">
                          <button class="action-btn edit-btn" @click="editRecipient(recipient, 'review_reply')">
                            <i class="uil uil-edit"></i>
                            Edit
                          </button>
                          <button class="action-btn delete-btn" @click="deleteRecipient(recipient, 'review_reply')">
                            <i class="uil uil-trash"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Negative Feedback Notifications -->
            <div class="settings-section">
              <div class="recipient-card">
                <div class="recipient-header">
                  <div class="recipient-title-group">
                    <h3 class="recipient-title">Negative Feedback Notifications</h3>
                    <span class="recipient-count">{{ negativeFeedbackRecipientsCount }}</span>
                  </div>
                  <button class="add-button" @click="showAddRecipientModal('negative_feedback')">
                    <i class="uil uil-plus"></i>
                    Add Recipient
                  </button>
                </div>
                <div class="negative-feedback-options">
                  <b-form-checkbox
                    :checked="negativeFeedbackEnabled"
                    :disabled="recipientSaving"
                    switch
                    @change="toggleNegativeFeedbackSetting('negative_feedback_enabled', $event)"
                  >
                    Email me immediately when a customer submits negative feedback
                  </b-form-checkbox>
                  <b-form-checkbox
                    :checked="negativeFeedbackNotifyAgency"
                    :disabled="recipientSaving || !negativeFeedbackEnabled"
                    switch
                    @change="toggleNegativeFeedbackSetting('negative_feedback_notify_agency', $event)"
                  >
                    Also send a copy to the agency
                  </b-form-checkbox>
                  <p class="negative-feedback-hint">
                    Negative feedback alerts are managed separately from new review notifications.
                    If no recipients are added, alerts are sent to the business account email.
                  </p>
                </div>
                <div v-if="negativeFeedbackRecipients.length" class="recipient-table-wrapper">
                  <table class="recipient-table">
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th class="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(recipient, index) in negativeFeedbackRecipients" :key="index">
                        <td>{{ recipient.first_name }}</td>
                        <td>{{ recipient.last_name }}</td>
                        <td>{{ recipient.email }}</td>
                        <td class="text-right">
                          <button class="action-btn edit-btn" @click="editRecipient(recipient, 'negative_feedback')">
                            <i class="uil uil-edit"></i>
                            Edit
                          </button>
                          <button class="action-btn delete-btn" @click="deleteRecipient(recipient, 'negative_feedback')">
                            <i class="uil uil-trash"></i>
                            Delete
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Employee Leaderboard Reports (Tap Engine / RepuAgency) -->
            <div v-if="showLeaderboardReportSettings" class="settings-section">
              <div class="recipient-card">
                <div class="recipient-header">
                  <div class="recipient-title-group">
                    <h3 class="recipient-title">Employee Leaderboard Reports</h3>
                  </div>
                </div>
                <p class="leaderboard-reports-description">
                  Schedule when leaderboard reports are emailed or texted to employees. Reports are sent in Eastern Time (EST).
                </p>
                <p class="leaderboard-reports-est">
                  Current EST: {{ currentEstTime }}
                </p>
                <ValidationObserver v-slot="{ handleSubmit, invalid, touched }" ref="reportsForm">
                  <form class="leaderboard-reports-form" @submit.prevent="handleSubmit(updateReportSettings)">
                    <div class="leaderboard-reports-field">
                      <label class="leaderboard-reports-label">Days to send leaderboard reports</label>
                      <div class="leaderboard-report-days">
                        <b-form-checkbox
                          v-for="(day, key) in user.business.settings.report_days"
                          :key="key"
                          v-model="day.sending"
                          size="sm"
                          class="leaderboard-report-day-checkbox"
                        >
                          {{ day.day }}
                        </b-form-checkbox>
                      </div>
                    </div>
                    <div class="leaderboard-reports-field time-td">
                      <label class="leaderboard-reports-label">Time to send leaderboard reports</label>
                      <b-time
                        v-model="user.business.settings.report_time"
                        :minutes-step="15"
                        hide-header
                      />
                    </div>
                    <div class="leaderboard-reports-actions">
                      <button
                        type="submit"
                        class="add-button"
                        :disabled="reportSettingsSaving || (invalid && touched)"
                      >
                        <b-spinner v-if="reportSettingsSaving" small />
                        <span v-else>Save schedule</span>
                      </button>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
          </div>
        </div>

        <!-- Connected Sites Tab -->
        <div
          v-if="showConnectedSites"
          v-show="activeTab === 'connected-sites' || activeTab === 'settings'"
          class="tab-panel connection-panel"
        >
          <div class="connected-sites-grid">
            <!-- Facebook & Instagram Card -->
            <div class="connection-card">
              <div class="connection-card-inner">
                <div class="connection-icons-wrapper">
                  <div class="connection-icon-fb">
                    <svg class="connection-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"></path>
                    </svg>
                  </div>
                  <div class="connection-icon-ig">
                    <svg class="connection-icon-svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"></path>
                    </svg>
                  </div>
                </div>
                <div class="connection-content-wrapper">
                  <h3 class="connection-title">Connect your location's Facebook Account</h3>
                  <a v-if="user.business.facebook_token && fb_valid" :href="FBRedirectLink" class="btn-connected-full">Connected</a>
                  <a v-else-if="user.business.facebook_token && !fb_valid" :href="FBRedirectLink" class="btn-danger-full">Integration Expired</a>
                  <a v-else ref="fbConnect" :href="FBRedirectLink" class="btn-primary-full">Connect</a>
                  <small v-if="user.business.facebook_token && !fb_valid" class="error-message-block">
                    There was an error validating the access token. This can happen when a user changes their password, or Facebook has changed their security sessions. Please re-connect your account.
                  </small>
                  <div v-if="fb_valid && user.business.facebook_token" class="connection-details">
                    <div class="connection-detail-group">
                      <p class="connection-detail-label">Facebook Page</p>
                      <select v-model="user.business.facebook_token.fb_page_id" class="connection-select-full" @change="updateFacebookPage">
                        <option v-for="fb_account in user.business.facebook_token.accounts" :key="fb_account.id" :value="fb_account.id">{{fb_account.name}}</option>
                      </select>
                    </div>
                    <div v-if="igAccounts" class="connection-detail-group">
                      <p class="connection-detail-label">Connected Instagram Account</p>
                      <p class="connection-detail-text">Name: {{igAccounts.name}}</p>
                      <p class="connection-detail-text">Username: @{{igAccounts.username}}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Google Business Profile Card -->
            <div class="connection-card">
              <div class="connection-card-inner">
                <div class="connection-icons-wrapper">
                  <div class="connection-icon-google">
                    <svg class="connection-icon-svg" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"></path>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"></path>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"></path>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"></path>
                    </svg>
                  </div>
                </div>
                <div class="connection-content-wrapper">
                  <h3 class="connection-title">Connect your Google Business Profile</h3>
                  <a v-if="user.business.location_id && !hasAccessTokenIssues" :href="gRedirectLink" class="btn-connected-full">Connected</a>
                  <a v-else-if="user.business.location_id && hasAccessTokenIssues" :href="gRedirectLink" class="btn-danger-full">Reconnect</a>
                  <a v-else :href="gRedirectLink" class="btn-primary-full">Connect</a>
                  <small v-if="hasAccessTokenIssues" class="error-message-block">
                    There was an issue with your Google Account access token. Please reconnect your Google Business Profile.
                  </small>
                  <div v-if="user.business.location_id && selected_account" class="connection-details">
                    <div class="connection-detail-group">
                      <p class="connection-detail-label">Google Business Page</p>
                      <select v-model="default_location" class="connection-select-full" @change="updateSettings">
                        <option v-for="(location,i) in selected_account.locations" :key="i" :value="location.id" :selected="location.is_default === 1">
                          {{location.title}} {{location.address !== null && location.address.hasOwnProperty('addressLines') && location.address.addressLines.length ? ' - ' + location.address.addressLines.join(' - ') : ''}}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- AI Review Reply Prompt Tab -->
        <div
          v-show="activeTab === 'ai-prompt'"
          class="tab-panel"
        >
          <div class="ai-prompt-section">
            <div class="ai-prompt-header">
              <h3 class="section-title">Review Reply Prompt</h3>
              <ValidationObserver v-slot="{ handleSubmit, invalid, touched }" ref="promptResetForm">
                <form class="text-right" @submit.prevent="handleSubmit(resetPromptSettings)">
                  <b-form-group>
                    <b-button
                      size="sm"
                      variant="danger"
                      :disabled="loadingSave || (invalid && touched)"
                      type="submit"
                    >
                      <b-spinner v-if="loadingSave" small />
                      <span v-else>Reset Prompt to Original</span>
                    </b-button>
                  </b-form-group>
                </form>
              </ValidationObserver>
            </div>
            <ValidationObserver v-slot="{ handleSubmit, invalid, touched }" ref="promptForm">
              <form class="authentication-form mt-2" @submit.prevent="handleSubmit(updatePromptSettings)">
                <textarea id="review-prompt" v-model="user.business.review_prompt" name="" rows="15" class="form-control mb-2"></textarea>
                <b-form-group>
                  <b-button
                    variant="primary"
                    class="btn-blue-save"
                    :disabled="loadingSave || (invalid && touched)"
                    type="submit"
                  >
                    <b-spinner v-if="loadingSave" small />
                    <span v-else>Save</span>
                  </b-button>
                </b-form-group>
              </form>
            </ValidationObserver>
          </div>
        </div>

        <!-- History Tab -->
        <div
          v-if="user.business && (user.business.is_social_review || user.business.is_social_fusion)"
          v-show="activeTab === 'history'"
          class="tab-panel"
        >
          <div class="history-section">
            <div class="history-card">
              <div class="history-header">
                <h3 class="history-title">System History</h3>
                <p class="history-description">All activities and events in your system</p>
              </div>
              <div class="history-content">
                <div v-if="retrieving_history" class="text-center py-8">
                  <b-spinner small /> Retrieving History
                </div>
                <div v-else>
                  <div v-if="socialHistories.length < 1" class="empty-history">
                    No Posting History
                  </div>
                  <div v-else class="history-list">
                    <div
                      v-for="history in socialHistories"
                      :key="history.id || `${history.platform}-${history.created_at}`"
                      class="history-item"
                    >
                      <div class="history-item-left">
                        <p class="history-action" :class="{ 'history-action-failed': history.status === 'failed' }">{{ historyActionLabel(history) }}</p>
                        <p v-if="historyDetailText(history)" class="history-detail">{{ historyDetailText(history) }}</p>
                        <p v-if="historyPostUrl(history)" class="history-link">
                          <a :href="historyPostUrl(history)" target="_blank" rel="noopener noreferrer">{{ historyPostUrl(history) }}</a>
                        </p>
                        <button
                          v-if="history.status === 'failed' && (history.platform === 'gbp' || history.platform === 'facebook')"
                          type="button"
                          class="history-retry"
                          :disabled="retryingPlatform === history.platform"
                          @click="retryPlatform(history)"
                        >
                          {{ retryingPlatform === history.platform ? 'Retrying…' : 'Retry ' + historyPlatformLabel(history.platform) }}
                        </button>
                      </div>
                      <div class="history-time">{{ historyPostedAt(history) }}</div>
                    </div>
                  </div>
                  <div v-if="socialHistories.length" class="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                    <span>Total {{ totalsocialHistories }}</span>
                    <div class="flex items-center space-x-2">
                      <button
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                        :disabled="datatable.queries.page === 1"
                        @click="datatable.queries.page > 1 && (datatable.queries.page--, onPageChanged(datatable.queries.page))"
                      >
                        «
                      </button>
                      <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80 request-review-btn" style="background-color: rgb(36, 99, 235); color: #ffffff;">
                        {{ datatable.queries.page }}
                      </div>
                      <button
                        class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                        :disabled="datatable.queries.page >= Math.ceil(totalsocialHistories / datatable.queries.per_page)"
                        @click="datatable.queries.page < Math.ceil(totalsocialHistories / datatable.queries.per_page) && (datatable.queries.page++, onPageChanged(datatable.queries.page))"
                      >
                        »
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Automated Review Requests Tab -->
        <div
          v-if="user && user.business && user.business.is_text_requester"
          v-show="activeTab === 'automated-requests'"
          class="tab-panel"
        >
          <div class="automated-requests-section">
            <div class="automated-requests-header">
              <h3 class="automated-requests-title">Automated Review Requests</h3>
              <p class="automated-requests-description">Configure automated review request campaigns</p>
            </div>
            <div class="automated-requests-content">
              <div class="automated-requests-tabs-wrapper">
                <div class="automated-requests-tabs">
                  <button
                    type="button"
                    class="sub-tab-button"
                    :class="{ active: activeSubTab === 'webhook' }"
                    @click="activeSubTab = 'webhook'"
                  >
                    Web Hook
                  </button>
                  <button
                    type="button"
                    class="sub-tab-button"
                    :class="{ active: activeSubTab === 'log' }"
                    @click="handleLogTabClick"
                  >
                    Log
                  </button>
                </div>

                <!-- Web Hook Sub Tab -->
                <div v-show="activeSubTab === 'webhook'" class="sub-tab-panel">
                  <div class="webhook-section">
                    <div class="webhook-section-item">
                      <h4 class="webhook-section-title">Zapier/Pabbly Review Request Creation Automation</h4>
                      <div class="webhook-url-wrapper">
                        <button type="button" class="method-badge-post">POST</button>
                        <code class="webhook-url-code">{{zapierUrl}}</code>
                      </div>
                    </div>
                    <div class="webhook-section-item">
                      <h4 class="webhook-section-title">Message Properties</h4>
                      <div class="properties-list">
                        <div class="property-item-new">
                          <div class="property-badges-new">
                            <button type="button" class="property-name-badge-new">first_name</button>
                            <button type="button" class="property-type-badge-new">string</button>
                            <button type="button" class="property-constraint-badge-new">max:250</button>
                          </div>
                          <p class="property-description-new">The first name of the contact</p>
                        </div>
                        <div class="property-item-new">
                          <div class="property-badges-new">
                            <button type="button" class="property-name-badge-new">last_name</button>
                            <button type="button" class="property-type-badge-new">string</button>
                            <button type="button" class="property-constraint-badge-new">max:250</button>
                          </div>
                          <p class="property-description-new">The last name of the contact</p>
                        </div>
                        <div class="property-item-new">
                          <div class="property-badges-new">
                            <button type="button" class="property-name-badge-new">email</button>
                            <button type="button" class="property-type-badge-new">string&lt;email&gt;</button>
                          </div>
                          <p class="property-description-new">The email address of the contact</p>
                        </div>
                        <div class="property-item-new">
                          <div class="property-badges-new">
                            <button type="button" class="property-name-badge-new">cell_phone</button>
                            <button type="button" class="property-type-badge-new">string&lt;valid cell phone number&gt;</button>
                          </div>
                          <p class="property-description-new">The cell phone number of the contact</p>
                        </div>
                        <div class="property-item-new">
                          <div class="property-badges-new">
                            <button type="button" class="property-name-badge-new">country</button>
                            <button type="button" class="property-type-badge-new">string</button>
                            <button type="button" class="property-constraint-badge-new">max:2</button>
                          </div>
                          <p class="property-description-new">
                            <strong>Optional:</strong> Specify the country code for the cell phone number. This is required when the phone number is not from the United States and is used by the phone validator to ensure accurate validation.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Log Sub Tab -->
                <div v-show="activeSubTab === 'log'" class="sub-tab-panel">
                  <div v-if="retrieving_review_request_log" class="text-center py-8">
                    <b-spinner small /> Retrieving Log
                  </div>
                  <div v-else>
                    <div v-if="reviewRequestLog.length < 1" class="empty-log">
                      No Log
                    </div>
                    <b-table
                      v-else
                      :items="reviewRequestLog"
                      :fields="reviewRequestDatatable.columns"
                      :sort-by.sync="reviewRequestDatatable.queries.sort_by"
                      :sort-desc.sync="reviewRequestDatatable.queries.sort_desc"
                      :per-page="reviewRequestDatatable.queries.per_page"
                      :no-local-sorting="true"
                      :borderless="true"
                    >
                      <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                      </div>
                      <template v-slot:cell(content)="row">
                        <!-- eslint-disable-next-line-->
                        <div v-html="row.item.nl2br_html"></div>
                      </template>
                    </b-table>
                    <div v-if="reviewRequestLog.length" class="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                      <span>Total {{ totalReviewRequestLog }}</span>
                      <div class="flex items-center space-x-2">
                        <button
                          class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                          :disabled="reviewRequestDatatable.queries.page === 1"
                          @click="reviewRequestDatatable.queries.page > 1 && (reviewRequestDatatable.queries.page--, onReviewRequestLogPageChanged(reviewRequestDatatable.queries.page))"
                        >
                          «
                        </button>
                        <div class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80 request-review-btn" style="background-color: rgb(36, 99, 235); color: #ffffff;">
                          {{ reviewRequestDatatable.queries.page }}
                        </div>
                        <button
                          class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                          :disabled="reviewRequestDatatable.queries.page >= Math.ceil(totalReviewRequestLog / reviewRequestDatatable.queries.per_page)"
                          @click="reviewRequestDatatable.queries.page < Math.ceil(totalReviewRequestLog / reviewRequestDatatable.queries.per_page) && (reviewRequestDatatable.queries.page++, onReviewRequestLogPageChanged(reviewRequestDatatable.queries.page))"
                        >
                          »
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <b-modal v-model="show_fb_modal" v-b-modal.modal-center :hide-header="!isImpersonating" :hide-footer="true" modal-class="location-connection-modal">
      <div class="container">
        <div class="row">
          <div class="col bg-white px-4 py-3">
            <label class="h3">Which business do you want to link to this location?</label>
            <div v-if="fb_valid">
              <div class="d-flex">
                <select v-if="user.business.facebook_token" v-model="user.business.facebook_token.fb_page_id" class="form-control" placeholder="Please Choose Page">
                  <option v-for="fb_account in user.business.facebook_token.accounts" :key="fb_account.id" :value="fb_account.id">{{fb_account.name}}</option>
                </select>
                <button v-if="user.business.facebook_token && user.business.facebook_token.fb_page_id !== initial_facebook_page" class="btn btn-primary ml-2" :disabled="user.business.facebook_token.is_saving" @click="updateFacebookPage">
                  <b-spinner v-if="user.business.facebook_token.is_saving" small />
                  <span v-else>Connect</span>
                </button>
              </div>
              <div v-if="igAccounts" class="mt-3">
                <strong class="d-block">Connected Instagram Account</strong>
                Name: {{igAccounts.name}}<br />
                Username: @{{igAccounts.username}}
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <b-modal v-model="show_modal" v-b-modal.modal-center modal-class="location-connection-modal" :hide-header="!isImpersonating" :hide-footer="true" :no-fade="true">
      <div class="container">
        <div class="row">
          <div class="col bg-white px-4 py-3">
            <ValidationObserver
              v-if="user && user.hasOwnProperty('google_accounts') && user.google_accounts.length"
              v-slot="{ handleSubmit, invalid, touched }"
              ref="settingsForm"
            >
              <form
                class="authentication-form mt-2"
                @submit.prevent="handleSubmit(updateSettings)"
              >
                <label class="h3">Which business do you want to link to this location?</label>
                <div v-if="user && user.google_accounts && user.google_accounts.length > 1" class="mb-1">
                  <select v-model="selected_account" class="form-control">
                    <option v-for="(account,i) in user.google_accounts" :key="i" :value="account" :selected="selected_account && selected_account.id === account.id">
                      {{account.account_name}}
                    </option>
                  </select>
                </div>
                <div v-if="selected_account" class="mt-2">
                  <select v-model="default_location" class="form-control">
                    <option v-if="!default_location" disabled :value="null">Select Page</option>
                    <option v-for="(location,i) in selected_account.locations" :key="i" :value="location.id" :selected="location.is_default === 1">
                      {{location.title}} {{location.address !== null && location.address.hasOwnProperty('addressLines') && location.address.addressLines.length ? ' - ' + location.address.addressLines.join(' - ') : ''}}
                    </option>
                  </select>
                </div>
                <div class="text-right mt-4">
                  <b-form-group>
                    <b-button
                      variant="success"
                      :disabled="loadingSave || (invalid && touched)"
                      type="submit"
                      class="px-4"
                      :squared="true"
                    >
                      <b-spinner v-if="loadingSave" small />
                      <span v-else>Connect</span>
                    </b-button>
                  </b-form-group>
                </div>
              </form>
            </ValidationObserver>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Add/Edit User Modal -->
    <b-modal v-model="showUserModal" :hide-footer="true" :title="editingUser ? 'Edit User' : 'Add User'" @close="resetUserForm">
      <ValidationObserver
        v-slot="{ handleSubmit, invalid, touched }"
        ref="userForm"
      >
        <form
          class="new-user-form"
          @submit.prevent="handleSubmit(saveUser)"
        >
          <div class="form-group">
            <label>First Name</label>
            <input
              v-model="userForm.first_name"
              type="text"
              class="form-control"
              placeholder="First Name"
              required
            />
          </div>
          <div class="form-group">
            <label>Last Name</label>
            <input
              v-model="userForm.last_name"
              type="text"
              class="form-control"
              placeholder="Last Name"
              required
            />
          </div>
          <div class="form-group">
            <label>Email</label>
            <input
              v-model="userForm.email"
              type="email"
              class="form-control"
              placeholder="Email"
              required
            />
          </div>
          <div class="text-right mt-4">
            <b-button
              variant="light"
              class="mr-2"
              @click="showUserModal = false"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              :disabled="loadingUser || (invalid && touched)"
              type="submit"
            >
              <b-spinner v-if="loadingUser" small />
              <span v-else>Save</span>
            </b-button>
          </div>
        </form>
      </ValidationObserver>
    </b-modal>

    <!-- Delete User Confirmation Modal -->
    <b-modal v-model="showDeleteUserModal" title="Delete User">
      <p v-if="selectedUser">
        Are you sure you want to delete user "{{ selectedUser.first_name }} {{ selectedUser.last_name }}"?
      </p>
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showDeleteUserModal = false">
          Cancel
        </b-button>
        <b-button variant="danger" @click="confirmDeleteUser">
          <b-spinner v-if="loadingDeleteUser" small />
          <span v-else>Delete</span>
        </b-button>
      </template>
    </b-modal>

    <!-- Change Password Modal -->
    <b-modal v-model="showChangePasswordModal" title="Change password" @close="resetChangePasswordForm">
      <ValidationObserver v-slot="{ handleSubmit, invalid, touched }" ref="changePasswordFormRef">
        <form @submit.prevent="handleSubmit(submitChangePassword)">
          <p v-if="userToChangePassword" class="text-muted small mb-3">
            Set a new password for {{ userToChangePassword.first_name }} {{ userToChangePassword.last_name }} ({{ userToChangePassword.email }}).
          </p>
          <b-form-group label="New password">
            <input
              v-model="changePasswordForm.password"
              type="password"
              class="form-control"
              placeholder="New password"
              required
            />
          </b-form-group>
          <b-form-group label="Confirm password">
            <input
              v-model="changePasswordForm.passwordConfirmation"
              type="password"
              class="form-control"
              placeholder="Confirm password"
              required
            />
          </b-form-group>
          <div class="d-flex justify-content-end gap-2">
            <b-button variant="light" @click="showChangePasswordModal = false">Cancel</b-button>
            <b-button variant="primary" type="submit" :disabled="loadingChangePassword || (invalid && touched)">
              <b-spinner v-if="loadingChangePassword" small />
              <span v-else>Change password</span>
            </b-button>
          </div>
        </form>
      </ValidationObserver>
    </b-modal>

    <!-- Add/Edit Recipient Modal -->
    <b-modal v-model="showRecipientModal" :hide-footer="true" :title="editingRecipient ? 'Edit Recipient' : 'Add Recipient'" @close="resetRecipientForm">
      <ValidationObserver
        v-slot="{ handleSubmit, invalid, touched }"
        ref="recipientFormModal"
      >
        <form
          class="new-recipient-form"
          @submit.prevent="handleSubmit(saveRecipient)"
        >
          <div class="form-group">
            <label>First Name</label>
            <input
              v-model="recipientForm.first_name"
              type="text"
              class="form-control"
              placeholder="First Name"
              required
            />
          </div>
          <div class="form-group">
            <label>Last Name</label>
            <input
              v-model="recipientForm.last_name"
              type="text"
              class="form-control"
              placeholder="Last Name"
              required
            />
          </div>
          <div class="form-group">
            <label>Email</label>
            <input
              v-model="recipientForm.email"
              type="email"
              class="form-control"
              placeholder="Email"
              required
            />
          </div>
          <div class="text-right mt-4">
            <b-button
              variant="light"
              class="mr-2"
              @click="showRecipientModal = false"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              :disabled="loadingRecipient || (invalid && touched)"
              type="submit"
            >
              <b-spinner v-if="loadingRecipient" small />
              <span v-else>Save</span>
            </b-button>
          </div>
        </form>
      </ValidationObserver>
    </b-modal>

    <!-- Delete Recipient Confirmation Modal -->
    <b-modal v-model="showDeleteRecipientModal" title="Delete Recipient">
      <p v-if="selectedRecipient">
        Are you sure you want to delete recipient "{{ selectedRecipient.first_name }} {{ selectedRecipient.last_name }}"?
      </p>
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showDeleteRecipientModal = false">
          Cancel
        </b-button>
        <b-button variant="danger" @click="confirmDeleteRecipient">
          <b-spinner v-if="loadingDeleteRecipient" small />
          <span v-else>Delete</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import 'vue-select/dist/vue-select.css'
import '../../../design/custom/components/_dashboard.scss'
import PasswordReset from '@components/Common/PasswordReset'

export default {
  components: {
    PasswordReset,
  },
  data() {
    return {
      business: {
        social_settings: {
          auto_sharing_settings: {
            fb_enabled: false,
            ig_enabled: false,
            gbp_enabled: false,
            minimum_rating: 5,
          },
        },
        social_fusion_settings: {
          auto_sharing_settings: {
            fb_enabled: false,
            ig_enabled: false,
            gbp_enabled: false,
            email_enabled: false,
          },
        },
      },
      activeTab: 'account',
      activeSubTab: 'webhook',
      recipientSaving: false,
      isDisabled: true,
      loadingSave: false,
      reportSettingsSaving: false,
      loadingAccountUpdate: false,
      selected_account: null,
      default_location: null,
      redirect_to_dashboard: false,
      // User modal state
      showUserModal: false,
      editingUser: false,
      loadingUser: false,
      loadingDeleteUser: false,
      showDeleteUserModal: false,
      showChangePasswordModal: false,
      userToChangePassword: null,
      changePasswordForm: { password: '', passwordConfirmation: '' },
      loadingChangePassword: false,
      selectedUser: null,
      userForm: {
        first_name: '',
        last_name: '',
        email: '',
      },
      // Recipient modal state
      showRecipientModal: false,
      editingRecipient: false,
      loadingRecipient: false,
      loadingDeleteRecipient: false,
      showDeleteRecipientModal: false,
      selectedRecipient: null,
      recipientType: null,
      recipientForm: {
        first_name: '',
        last_name: '',
        email: '',
      },
      user: {
        business: {
          settings: {},
          email_settings: {
            new_review_emails: null,
            new_social_post_emails: null,
            new_review_replied_emails: null,
          },
          twilio_settings: {
            prequalify_accept_msg: 'Thank you so much. Would you mind leaving us a review here - [google_review_link]?',
            prequalify_fail_msg: 'Thank you for taking the time to let us know. You can check out our Google Business Profile Here - [google_business_profile_link]?',
            prequalify_initial_msg: 'Please rate us by replying with a number from 1 to 5.',
          },
        },
      },
      initial_facebook_page: '',
      show_modal: false,
      show_fb_modal: false,
      loadingSettingsSave: false,
      time_array: ['12am', '1am'],
      connected_sites_tab: false,
      fb_valid: true,
      retrieving_history: false,
      retryingPlatform: null,
      retrieving_review_request_log: false,
      datatable: {
        items: [],
        columns: [
          { key: 'platform', label: 'Platform', sortable: true },
          { key: 'front_date', label: 'Posted Date', sortable: true },
          { key: 'link', label: 'Link', class: 'link' },
        ],
        queries: {
          per_page: 10,
          page: 1,
          status: 'active',
          search: null,
          sort_by: 'created_at',
          sort_desc: false,
        },
      },
      reviewRequestDatatable: {
        items: [],
        columns: [
          { key: 'type', label: 'Type', sortable: true },
          { key: 'front_date', label: 'Sent Date', sortable: true },
          { key: 'sent_to', label: 'Sent To' },
          { key: 'content', label: 'Content' },
        ],
        queries: {
          per_page: 10,
          page: 1,
          status: 'active',
          search: null,
          sort_by: 'created_at',
          sort_desc: false,
        },
      },
    }
  },
  computed: {
    isAdmin() {
      return localStorage.getItem('main_access_role') === 'Admin'
    },
    isRepuAgency() {
      return this.user && this.user.role === 'RepuAgency'
    },
    showConnectedSites() {
      return !!(this.user && this.user.business)
    },
    zapierUrl() {
      return (
        process.env.VUE_APP_BASE_API +
        'api/v1/create-contact/' +
        this.user.business.unique_id +
        '-' +
        this.user.business.id
      )
    },
    isImpersonating() {
      return this.$store.getters['auth/isImpersonating']
    },
    gRedirectLink() {
      return (process.env.NODE_ENV === 'production' ? process.env.VUE_APP_BASE_API : process.env.VUE_APP_BASE_API_LOCAL) + 'api/v1/auth-redirect/' + this.user.id + '/' + this.business.id
    },
    FBRedirectLink() {
      const apiUrl = process.env.VUE_APP_BASE_API + 'api/v1'
      return apiUrl + '/fb-redirect/' + this.user.id + '/' + this.business.id
    },
    IGRedirectLink() {
      const apiUrl = process.env.VUE_APP_BASE_API + 'api/v1'
      return apiUrl + '/ig-redirect/' + this.user.id + '/' + this.business.id
    },
    igAccounts() {
      if (this.user?.business?.facebook_token?.accounts) {
        const foundAccount = this.user.business.facebook_token.accounts.find((o) => o.id === this.user.business.facebook_token.fb_page_id)
        return foundAccount ? foundAccount.instagram_business_account : null
      }
      return null
    },
    FBLongLivedToken() {
      if (this.user?.business?.facebook_token?.accounts) {
        const foundAccount = this.user.business.facebook_token.accounts.find((o) => o.id === this.user.business.facebook_token.fb_page_id)
        return foundAccount ? foundAccount.access_token : null
      }
      return null
    },
    socialHistories() {
      return this.$store.getters['socialHistories/all']
    },
    totalsocialHistories() {
      return this.$store.getters['socialHistories/total']
    },
    reviewRequestLog() {
      return this.$store.getters['reviewRequestLog/all']
    },
    totalReviewRequestLog() {
      return this.$store.getters['reviewRequestLog/total']
    },
    fromFacebook() {
      return this.$route.query.from_fb === 'true'
    },
    hasAccessTokenIssues() {
      if (this.user?.business?.location?.has_access_token_issues) return true
      return this.selected_account && this.selected_account.locations.some((account) => account.has_access_token_issues)
    },
    users() {
      // Get users from created_team_users
      return this.user?.created_team_users || []
    },
    usersCount() {
      return this.users.length
    },
    newReviewRecipients() {
      // Parse email recipients from comma-separated string
      if (!this.user?.business?.email_settings?.new_review_emails) return []
      return this.parseEmailRecipients(this.user.business.email_settings.new_review_emails)
    },
    newReviewRecipientsCount() {
      return this.newReviewRecipients.length
    },
    socialPostRecipients() {
      if (!this.user?.business?.email_settings?.new_social_post_emails) return []
      return this.parseEmailRecipients(this.user.business.email_settings.new_social_post_emails)
    },
    socialPostRecipientsCount() {
      return this.socialPostRecipients.length
    },
    reviewReplyRecipients() {
      if (!this.user?.business?.email_settings?.new_review_replied_emails) return []
      return this.parseEmailRecipients(this.user.business.email_settings.new_review_replied_emails)
    },
    reviewReplyRecipientsCount() {
      return this.reviewReplyRecipients.length
    },
    negativeFeedbackRecipients() {
      if (!this.user?.business?.email_settings?.negative_feedback_emails) return []
      return this.parseEmailRecipients(this.user.business.email_settings.negative_feedback_emails)
    },
    negativeFeedbackRecipientsCount() {
      return this.negativeFeedbackRecipients.length
    },
    negativeFeedbackEnabled() {
      return this.user?.business?.email_settings?.negative_feedback_enabled !== false
    },
    negativeFeedbackNotifyAgency() {
      return !!this.user?.business?.email_settings?.negative_feedback_notify_agency
    },
    currentEstTime() {
      return new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
    },
    showLeaderboardReportSettings() {
      const business = this.user?.business
      if (!business) return false

      if (business.is_tap_engine) return true
      if (this.user?.has_repu_agency) return true

      const owner = business.owner
      if (owner?.has_repu_agency) return true
      if (owner?.agency?.free_fee) return true

      if (this.user?.has_te || owner?.has_te) return true
      if (this.userHasProductAccess('has_te')) return true

      return false
    },
  },
  mounted() {
    this.user = Vue.util.extend({}, this.$store.getters['auth/user'])
    // Ensure email_settings is initialized
    if (!this.user.business.email_settings) {
      this.$set(this.user.business, 'email_settings', {
        new_review_emails: null,
        new_social_post_emails: null,
        new_review_replied_emails: null,
      })
    }
    if (this.user.google_accounts && this.user.google_accounts.length === 1) {
      this.selected_account = this.user.google_accounts[0]
    } else if (this.user.google_accounts && this.user.google_accounts.length > 1) {
      const locationAccount = this.user.google_accounts.filter((acct) => {
        return acct.locations.find((loc) => {
          if (loc.is_default) {
            this.default_location = loc.id
          }
          return loc.is_default
        })
      })
      if (locationAccount.length) {
        this.selected_account = locationAccount[0]
      }
    }
    if (this.selected_account) {
      this.selected_account.locations.forEach((loc) => {
        if (loc.is_default === 1) {
          this.default_location = loc.id
        }
      })
    }
    this.business = Vue.util.extend({}, this.user.business)
    this.show_modal = this.user && this.user.google_accounts && this.user.google_accounts.length && !this.default_location
    this.show_fb_modal = this.fromFacebook && this.user?.business?.facebook_token?.accounts.length > 0 && this.user.business?.facebook_token?.fb_page_id === null

    if (this.default_location == null) {
      this.redirect_to_dashboard = true
    }
    if (window.location.search.includes('t=cs')) {
      this.connected_sites_tab = true
      this.activeTab = 'connected-sites'
    }
    // Push people to connected Sites tab if the token doesn't exist
    if (this.user && this.user.google_accounts && this.user.google_accounts.length && this.default_location && this.user?.business?.is_social_review && this.user?.business?.facebook_token === null) {
      this.connected_sites_tab = true
      this.activeTab = 'connected-sites'
    }
    if (this.user?.business?.is_social_review && this.user?.business?.facebook_token) {
      this.getCurrentFBConnection()
      this.initial_facebook_page = this.user.business.facebook_token ? this.user.business.facebook_token.fb_page_id : null
    }
    if (this.user?.business?.is_social_fusion && this.user?.business?.facebook_token) {
      this.getCurrentFBConnection()
      this.initial_facebook_page = this.user.business.facebook_token ? this.user.business.facebook_token.fb_page_id : null
    }
    // Add gbp_enabled if setting already exists and it is absent
    if (this.business?.social_settings?.auto_sharing_settings && !('gbp_enabled' in this.business.social_settings.auto_sharing_settings)) {
      this.$set(this.user.business.social_settings.auto_sharing_settings, 'gbp_enabled', false)
    }

    if (this.business?.social_fusion_settings?.auto_sharing_settings && !('gbp_enabled' in this.business.social_fusion_settings.auto_sharing_settings)) {
      this.$set(this.user.business.social_fusion_settings.auto_sharing_settings, 'gbp_enabled', false)
    }
    if (this.showLeaderboardReportSettings) {
      this.ensureLeaderboardReportSettings()
    }
  },
  methods: {
    userHasProductAccess(productType) {
      if (!this.user?.user_subscriptions) return false
      return this.user.user_subscriptions.some(
        (sub) =>
          Array.isArray(sub.agency_subscription?.modules) &&
          sub.agency_subscription.modules.includes(productType)
      )
    },
    ensureLeaderboardReportSettings() {
      if (!this.user.business.settings || typeof this.user.business.settings !== 'object') {
        this.$set(this.user.business, 'settings', {})
      }
      const defaultDays = [
        { day: 'Monday', sending: true },
        { day: 'Tuesday', sending: true },
        { day: 'Wednesday', sending: true },
        { day: 'Thursday', sending: true },
        { day: 'Friday', sending: true },
        { day: 'Saturday', sending: true },
        { day: 'Sunday', sending: true },
      ]
      if (!this.user.business.settings.report_days || !this.user.business.settings.report_days.length) {
        this.$set(this.user.business.settings, 'report_days', defaultDays)
      }
      if (!this.user.business.settings.report_time) {
        this.$set(this.user.business.settings, 'report_time', '13:00')
      }
    },
    parseEmailRecipients(emailString) {
      if (!emailString) return []
      const emails = emailString.split(',').map((e) => e.trim()).filter((e) => e)
      return emails.map((email) => {
        // Try to parse name from email format like "Name <email@example.com>"
        const match = email.match(/^(.+?)\s*<(.+?)>$/)
        if (match) {
          const nameParts = match[1].trim().split(/\s+/)
          return {
            first_name: nameParts[0] || '',
            last_name: nameParts.slice(1).join(' ') || '',
            email: match[2].trim(),
          }
        }
        // If no name, try to extract from email
        const emailParts = email.split('@')[0].split(/[._-]/)
        return {
          first_name: emailParts[0] || '',
          last_name: emailParts.slice(1).join(' ') || '',
          email: email.trim(),
        }
      })
    },
    historyActionLabel(history) {
      if (history.action_label) {
        return history.action_label
      }
      const platform = this.historyPlatformLabel(history.platform)
      if (history.post_kind === 'social_post' || history.source === 'fusion_post') {
        return `Social Post Posted to ${platform}`
      }
      return `Review Posted to ${platform}`
    },
    historyDetailText(history) {
      if (history.detail) {
        return history.detail
      }
      if (history.post_kind === 'review' && history.author) {
        return `Review by ${history.author}`
      }
      return null
    },
    historyPostUrl(history) {
      if (history.post_url) {
        return history.post_url
      }
      const platform = history.platform
      const pageId = history.page_id
      if (platform === 'facebook' && pageId) {
        return `https://www.facebook.com/${pageId}`
      }
      if (platform === 'instagram' && pageId) {
        return `https://www.instagram.com/p/${pageId}`
      }
      return null
    },
    historyPostedAt(history) {
      if (history.posted_at_display) {
        return history.posted_at_display
      }
      if (history.front_date) {
        return history.front_date
      }
      if (!history.created_at) {
        return 'N/A'
      }
      try {
        return new Date(history.created_at).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      } catch (e) {
        return 'N/A'
      }
    },
    historyPlatformLabel(platform) {
      if (platform === 'facebook') return 'Facebook'
      if (platform === 'instagram') return 'Instagram'
      if (platform === 'gbp') return 'Google Business Profile'
      return platform ? String(platform).charAt(0).toUpperCase() + String(platform).slice(1) : ''
    },
    formatTimeAgo(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return 'N/A'
        const now = new Date()
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)

        if (diffMins < 1) {
          return 'Just now'
        } else if (diffMins < 60) {
          return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`
        } else if (diffHours < 24) {
          const remainingMins = diffMins % 60
          if (remainingMins > 0) {
            return `${diffHours}h ${remainingMins} min ago`
          }
          return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
        } else if (diffDays < 7) {
          return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`
        } else {
          const diffWeeks = Math.floor(diffDays / 7)
          if (diffWeeks < 4) {
            return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`
          } else {
            const diffMonths = Math.floor(diffDays / 30)
            if (diffMonths < 12) {
              return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`
            } else {
              const diffYears = Math.floor(diffDays / 365)
              return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`
            }
          }
        }
      } catch (e) {
        return 'N/A'
      }
    },
    handleHistoryTabClick() {
      this.activeTab = 'history'
      this.getHistory()
    },
    handleLogTabClick() {
      this.activeSubTab = 'log'
      this.getReviewRequestLog()
    },
    showAddUserModal() {
      this.editingUser = false
      this.resetUserForm()
      this.showUserModal = true
    },
    editUser(userItem) {
      this.editingUser = true
      this.userForm = {
        first_name: userItem.first_name || '',
        last_name: userItem.last_name || '',
        email: userItem.email || '',
        id: userItem.id,
      }
      this.showUserModal = true
    },
    deleteUser(userItem) {
      this.selectedUser = userItem
      this.showDeleteUserModal = true
    },
    openChangePasswordModal(userItem) {
      this.userToChangePassword = userItem
      this.changePasswordForm = { password: '', passwordConfirmation: '' }
      this.showChangePasswordModal = true
      this.$nextTick(() => this.$refs.changePasswordFormRef?.reset())
    },
    resetChangePasswordForm() {
      this.changePasswordForm = { password: '', passwordConfirmation: '' }
      this.userToChangePassword = null
      this.showChangePasswordModal = false
      this.$nextTick(() => this.$refs.changePasswordFormRef?.reset())
    },
    submitChangePassword() {
      if (!this.userToChangePassword) return
      const { password, passwordConfirmation } = this.changePasswordForm
      if (!password || password.length < 8) {
        Vue.$toast.open({
          message: 'Password must be at least 8 characters',
          type: 'error',
          duration: 5000,
        })
        return
      }
      if (password !== passwordConfirmation) {
        Vue.$toast.open({
          message: 'Password and confirmation do not match',
          type: 'error',
          duration: 5000,
        })
        return
      }
      this.loadingChangePassword = true
      this.$store
        .dispatch('user/changePassword', {
          user_id: this.userToChangePassword.id,
          password,
          password_confirmation: passwordConfirmation,
        })
        .then(() => {
          this.loadingChangePassword = false
          this.resetChangePasswordForm()
          Vue.$toast.open({
            message: 'Password updated successfully',
            type: 'success',
            duration: 5000,
          })
        })
        .catch((err) => {
          this.loadingChangePassword = false
          Vue.$toast.open({
            message:
              err.response?.data?.message ||
              err.response?.data?.errors?.password?.join?.(', ') ||
              'Failed to update password',
            type: 'error',
            duration: 5000,
          })
        })
    },
    confirmDeleteUser() {
      if (!this.selectedUser) return
      this.loadingDeleteUser = true
      // Get the team that contains this user
      const team = this.user.teams?.find((t) =>
        t.team_users.some((u) => u.id === this.selectedUser.id)
      )
      if (!team) {
        this.loadingDeleteUser = false
        Vue.$toast.open({
          message: 'User not found in any team',
          type: 'error',
          duration: 5000,
        })
        return
      }
      this.$store
        .dispatch('user/removeTeamUser', {
          userId: this.selectedUser.id,
          teamId: team.id,
        })
        .then((res) => {
          this.user.teams = res.data.user.teams
          this.user.created_team_users = res.data.user.created_team_users
          this.showDeleteUserModal = false
          this.selectedUser = null
          this.loadingDeleteUser = false
          Vue.$toast.open({
            message: 'User deleted successfully',
            type: 'success',
            duration: 5000,
          })
        })
        .catch((err) => {
          console.error(err)
          this.loadingDeleteUser = false
          Vue.$toast.open({
            message: 'Failed to delete user',
            type: 'error',
            duration: 5000,
          })
        })
    },
    resetUserForm() {
      this.userForm = {
        first_name: '',
        last_name: '',
        email: '',
      }
      this.editingUser = false
    },
    saveUser() {
      this.loadingUser = true
      const businessTeamId = this.user.business?.team_id || this.user.business?.team?.id
        // Business settings only grant access to this business — never pass the logged-in user's full team list
        const teamIds = businessTeamId ? [businessTeamId] : []
        const userData = {
          first_name: this.userForm.first_name,
          last_name: this.userForm.last_name,
          email: this.userForm.email,
          role: this.user.role,
          agency: this.user.team_agency,
          selectedTeams: teamIds,
        }
      if (this.editingUser && this.userForm.id) {
        this.$store
          .dispatch('user/updateTeamUser', {
            userId: this.userForm.id,
            first_name: this.userForm.first_name,
            last_name: this.userForm.last_name,
            email: this.userForm.email,
          })
          .then((res) => {
            const updated = res.data.user
            this.user.teams = updated.teams
            this.user.created_team_users = updated.created_team_users
            const storeUser = this.$store.getters['auth/user']
            if (storeUser) {
              storeUser.teams = updated.teams
              storeUser.created_team_users = updated.created_team_users
            }
            this.loadingUser = false
            this.showUserModal = false
            this.resetUserForm()
            Vue.$toast.open({
              message: 'User updated successfully',
              type: 'success',
              duration: 5000,
            })
          })
          .catch((err) => {
            console.error(err)
            this.loadingUser = false
            const emailError = err.response?.data?.errors?.email
            Vue.$toast.open({
              message:
                (Array.isArray(emailError) ? emailError[0] : emailError) ||
                err.response?.data?.message ||
                'Failed to update user',
              type: 'error',
              duration: 5000,
            })
          })
        return
      }
      this.$store
        .dispatch('user/addTeamUser', userData)
        .then((res) => {
          this.user.teams = res.data.user.teams
          this.user.created_team_users = res.data.user.created_team_users
          this.loadingUser = false
          this.showUserModal = false
          this.resetUserForm()
          Vue.$toast.open({
            message: 'User added successfully',
            type: 'success',
            duration: 5000,
          })
        })
        .catch((err) => {
          console.error(err)
          this.loadingUser = false
          Vue.$toast.open({
            message: err.response?.data?.message || 'Failed to add user',
            type: 'error',
            duration: 5000,
          })
        })
    },
    showAddRecipientModal(type) {
      this.recipientType = type
      this.editingRecipient = false
      this.resetRecipientForm()
      this.showRecipientModal = true
    },
    editRecipient(recipient, type) {
      this.recipientType = type
      this.editingRecipient = true
      this.recipientForm = {
        first_name: recipient.first_name || '',
        last_name: recipient.last_name || '',
        email: recipient.email || '',
        originalEmail: recipient.email,
      }
      this.showRecipientModal = true
    },
    deleteRecipient(recipient, type) {
      this.selectedRecipient = recipient
      this.recipientType = type
      this.showDeleteRecipientModal = true
    },
    confirmDeleteRecipient() {
      if (!this.selectedRecipient || !this.recipientType) return
      this.removeRecipientFromList(this.selectedRecipient, this.recipientType)
      this.showDeleteRecipientModal = false
      this.selectedRecipient = null
      this.recipientType = null
    },
    resetRecipientForm() {
      this.recipientForm = {
        first_name: '',
        last_name: '',
        email: '',
      }
      this.editingRecipient = false
    },
    saveRecipient() {
      this.loadingRecipient = true
      let currentEmails = ''
      if (this.recipientType === 'new_review') {
        currentEmails = this.user.business.email_settings.new_review_emails || ''
      } else if (this.recipientType === 'social_post') {
        currentEmails = this.user.business.email_settings.new_social_post_emails || ''
      } else if (this.recipientType === 'review_reply') {
        currentEmails = this.user.business.email_settings.new_review_replied_emails || ''
      } else if (this.recipientType === 'negative_feedback') {
        currentEmails = this.user.business.email_settings.negative_feedback_emails || ''
      }
      // Parse existing recipients
      const existingRecipients = this.parseEmailRecipients(currentEmails)
      // Remove the old recipient if editing
      if (this.editingRecipient && this.recipientForm.originalEmail) {
        const index = existingRecipients.findIndex(
          (r) => r.email === this.recipientForm.originalEmail
        )
        if (index > -1) {
          existingRecipients.splice(index, 1)
        }
      }
      // Add the new recipient
      existingRecipients.push({
        first_name: this.recipientForm.first_name,
        last_name: this.recipientForm.last_name,
        email: this.recipientForm.email,
      })
      // Convert back to email string format
      const emailString = existingRecipients
        .map((r) => this.formatRecipientString(r.first_name, r.last_name, r.email))
        .join(', ')
      // Update the email settings using Vue.set for reactivity
      if (this.recipientType === 'new_review') {
        this.$set(this.user.business.email_settings, 'new_review_emails', emailString)
      } else if (this.recipientType === 'social_post') {
        this.$set(this.user.business.email_settings, 'new_social_post_emails', emailString)
      } else if (this.recipientType === 'review_reply') {
        this.$set(this.user.business.email_settings, 'new_review_replied_emails', emailString)
      } else if (this.recipientType === 'negative_feedback') {
        this.$set(this.user.business.email_settings, 'negative_feedback_emails', emailString)
      }
      // Save to backend
      this.updateEmailSettings()
        .then(() => {
          this.loadingRecipient = false
          this.showRecipientModal = false
          this.resetRecipientForm()
          Vue.$toast.open({
            message: 'Recipient saved successfully',
            type: 'success',
            duration: 5000,
          })
        })
        .catch(() => {
          this.loadingRecipient = false
          Vue.$toast.open({
            message: 'Failed to save recipient',
            type: 'error',
            duration: 5000,
          })
        })
    },
    removeRecipientFromList(recipient, type) {
      let currentEmails = ''
      if (type === 'new_review') {
        currentEmails = this.user.business.email_settings.new_review_emails || ''
      } else if (type === 'social_post') {
        currentEmails = this.user.business.email_settings.new_social_post_emails || ''
      } else if (type === 'review_reply') {
        currentEmails = this.user.business.email_settings.new_review_replied_emails || ''
      } else if (type === 'negative_feedback') {
        currentEmails = this.user.business.email_settings.negative_feedback_emails || ''
      }
      const existingRecipients = this.parseEmailRecipients(currentEmails)
      const index = existingRecipients.findIndex((r) => r.email === recipient.email)
      if (index > -1) {
        existingRecipients.splice(index, 1)
        const emailString = existingRecipients
          .map((r) => this.formatRecipientString(r.first_name, r.last_name, r.email))
          .join(', ')
        if (type === 'new_review') {
          this.$set(this.user.business.email_settings, 'new_review_emails', emailString)
        } else if (type === 'social_post') {
          this.$set(this.user.business.email_settings, 'new_social_post_emails', emailString)
        } else if (type === 'review_reply') {
          this.$set(this.user.business.email_settings, 'new_review_replied_emails', emailString)
        } else if (type === 'negative_feedback') {
          this.$set(this.user.business.email_settings, 'negative_feedback_emails', emailString)
        }
        this.updateEmailSettings()
          .then(() => {
            Vue.$toast.open({
              message: 'Recipient deleted successfully',
              type: 'success',
              duration: 5000,
            })
          })
          .catch(() => {
            Vue.$toast.open({
              message: 'Failed to delete recipient',
              type: 'error',
              duration: 5000,
            })
          })
      }
    },
    toggleNegativeFeedbackSetting(key, value) {
      const previous = this.user.business.email_settings[key]
      this.$set(this.user.business.email_settings, key, value)
      this.updateEmailSettings()
        .then(() => {
          Vue.$toast.open({
            message: 'Notification preferences saved',
            type: 'success',
            duration: 5000,
          })
        })
        .catch(() => {
          this.$set(this.user.business.email_settings, key, previous)
          Vue.$toast.open({
            message: 'Failed to save notification preferences',
            type: 'error',
            duration: 5000,
          })
        })
    },
    formatRecipientString(firstName, lastName, email) {
      const name = `${firstName} ${lastName}`.trim()
      if (name) {
        return `${name} <${email}>`
      }
      return email
    },
    phoneUpdated(phone) {
      this.user.business.twilio_settings.phoneObject = phone
    },
    onPageChanged(page) {
      this.datatable.queries.page = page
      this.getHistory()
    },
    onReviewRequestLogPageChanged(page) {
      this.reviewRequestDatatable.queries.page = page
      this.getReviewRequestLog()
    },
    updateFacebookPage() {
      this.user.business.facebook_token.is_saving = true
      const requestParam = {
        business_id: this.user.business.id,
        fb_page_id: this.user.business.facebook_token.fb_page_id,
        fb_page_access_token: this.FBLongLivedToken,
        token_id: this.user.business.facebook_token.id,
      }
      this.$store
        .dispatch('business/setFBPage', requestParam)
        .then(() => {
          this.initial_facebook_page = requestParam.fb_page_id
          this.user.business.facebook_token.is_saving = false
          this.show_fb_modal = false
        })
        .catch((err) => {
          console.error(err)
          this.user.business.facebook_token.is_saving = false
          this.show_fb_modal = false
        })
    },
    getCurrentFBConnection() {
      this.$store
        .dispatch('business/getCurrentConnection', {
          token_id: this.user.business.facebook_token.id,
        })
        .then((resp) => {
          this.user.business.facebook_token.accounts = resp
        })
        .catch((err) => {
          this.fb_valid = false
          console.error(err)
        })
    },
    retryPlatform(history) {
      this.retryingPlatform = history.platform
      this.$store
        .dispatch('socialHistories/retryPlatform', {
          businessId: this.user.business.id,
          platform: history.platform,
        })
        .then((resp) => {
          this.retryingPlatform = null
          this.$bvToast.toast(resp.message || 'Post published.', {
            title: 'Social post',
            variant: 'success',
            solid: true,
          })
          this.getHistory()
        })
        .catch((err) => {
          this.retryingPlatform = null
          const message = err?.response?.data?.message || 'Retry failed.'
          this.$bvToast.toast(message, {
            title: 'Social post',
            variant: 'danger',
            solid: true,
          })
          this.getHistory()
        })
    },
    getHistory() {
      this.retrieving_history = true
      this.$store
        .dispatch('socialHistories/getAll', {
          queries: this.datatable.queries,
          page: 'index',
          businessId: this.user.business.id,
        })
        .then((resp) => {
          console.log(resp)
          this.retrieving_history = false
        })
        .catch((err) => {
          this.retrieving_history = false
          console.error(err)
        })
    },
    getReviewRequestLog() {
      this.retrieving_review_request_log = true
      this.$store
        .dispatch('reviewRequestLog/getAll', {
          queries: this.reviewRequestDatatable.queries,
          page: 'logs',
          businessId: this.user.business.id,
        })
        .then((resp) => {
          console.log(resp)
          this.retrieving_review_request_log = false
        })
        .catch((err) => {
          this.retrieving_review_request_log = false
          console.error(err)
        })
    },
    updateEmailSettings() {
      return new Promise((resolve, reject) => {
        this.recipientSaving = true
        this.$store
          .dispatch('business/editSettings', {
            email_settings: {
              new_review_emails: this.user.business.email_settings.new_review_emails,
              new_social_post_emails: this.user.business.email_settings.new_social_post_emails,
              new_review_replied_emails: this.user.business.email_settings.new_review_replied_emails,
              negative_feedback_emails: this.user.business.email_settings.negative_feedback_emails,
              negative_feedback_enabled: this.negativeFeedbackEnabled,
              negative_feedback_notify_agency: this.negativeFeedbackNotifyAgency,
            },
            business_id: this.user.business.id,
            settings_form: 'recipient_form_settings',
          })
          .then((updatedUser) => {
            // Update the local user object with the response from the store
            this.user = Vue.util.extend({}, updatedUser)
            // Ensure email_settings is initialized
            if (!this.user.business.email_settings) {
              this.$set(this.user.business, 'email_settings', {
                new_review_emails: null,
                new_social_post_emails: null,
                new_review_replied_emails: null,
              })
            }
            this.recipientSaving = false
            resolve(updatedUser)
          })
          .catch((err) => {
            console.error(err)
            this.recipientSaving = false
            reject(err)
          })
      })
    },
    resetPromptSettings() {
      this.user.business.review_prompt = null
      this.updatePromptSettings()
    },
    updatePromptSettings() {
      this.loadingSave = true
      const requestParam = {
        business_id: this.user.business.id,
        prompt: this.user.business.review_prompt,
        settings_form: 'prompt_form_settings',
      }
      this.$store
        .dispatch('business/editSettings', requestParam)
        .then((user) => {
          this.user.business.review_prompt = user.business.review_prompt
          this.loadingSave = false
        })
        .catch((err) => {
          console.error(err)
          this.loadingSave = false
        })
    },
    updateReportSettings() {
      this.reportSettingsSaving = true
      const requestParam = {
        business_id: this.user.business.id,
        settings: this.user.business.settings,
      }
      this.$store
        .dispatch('business/editSettings', requestParam)
        .then(() => {
          this.reportSettingsSaving = false
          Vue.$toast.open({
            message: 'Leaderboard report schedule saved',
            type: 'success',
            duration: 5000,
          })
        })
        .catch((err) => {
          console.error(err)
          this.reportSettingsSaving = false
          Vue.$toast.open({
            message: 'Failed to save leaderboard report schedule',
            type: 'error',
            duration: 5000,
          })
        })
    },
    updateTwilioSettings() {
      this.loadingSave = true
      const requestParam = {
        business_id: this.user.business.id,
        settings: this.user.business.twilio_settings,
      }
      this.$store
        .dispatch('business/editTwilioSettings', requestParam)
        .then(() => {
          this.loadingSave = false
        })
        .catch((err) => {
          console.error(err)
          this.loadingSave = false
        })
    },
    updateSocialSettings() {
      this.loadingSettingsSave = true
      const requestParam = {
        business_id: this.user.business.id,
        settings: this.user.business.social_settings,
      }
      this.$store
        .dispatch('business/editSocialSettings', requestParam)
        .then(() => {
          this.loadingSettingsSave = false
        })
        .catch((err) => {
          console.error(err)
          this.loadingSettingsSave = false
        })
    },
    updateSocialFusionSettings() {
      this.loadingSettingsSave = true
      const requestParam = {
        business_id: this.user.business.id,
        settings: this.user.business.social_fusion_settings,
      }
      this.$store
        .dispatch('business/editSocialFusionSettings', requestParam)
        .then(() => {
          this.loadingSettingsSave = false
        })
        .catch((err) => {
          console.error(err)
          this.loadingSettingsSave = false
        })
    },
    updateSettings() {
      this.loadingSave = true
      const requestParam = {
        selectedAccount: this.selected_account.id,
        defaultLocation: this.default_location,
        business_id: this.user.business.id,
      }
      this.$store
        .dispatch('business/editSettings', requestParam)
        .then(() => {
          this.loadingSave = false
          if (this.default_location !== null && this.redirect_to_dashboard) {
            this.$router.push({ name: 'new-business.dashboard' })
          }
        })
        .catch((err) => {
          console.error(err)
          this.loadingSave = false
        })
    },
    updateAccountProfile() {
      this.loadingAccountUpdate = true
      this.$store
        .dispatch('auth/updateProfile', this.user)
        .then(() => {
          this.user = Vue.util.extend({}, this.$store.getters['auth/user'])
          this.loadingAccountUpdate = false
          Vue.$toast.open({
            message: 'Profile updated successfully',
            type: 'success',
            duration: 5000,
          })
        })
        .catch((err) => {
          if (err.response?.data?.errors && this.$refs.accountProfileForm) {
            this.$refs.accountProfileForm.setErrors(err.response.data.errors)
          }
          this.loadingAccountUpdate = false
          Vue.$toast.open({
            message: err.response?.data?.message || 'Failed to update profile',
            type: 'error',
            duration: 5000,
          })
        })
    },
    handleChange() {
      if (
        this.user.business.social_fusion_settings.auto_sharing_settings.fb_enabled ||
        this.user.business.social_fusion_settings.auto_sharing_settings.ig_enabled ||
        this.user.business.social_fusion_settings.auto_sharing_settings.gbp_enabled
      ) {
        if (this.user.business.social_fusion_settings.auto_sharing_settings.email_enabled) {
          this.$set(this.user.business.social_fusion_settings.auto_sharing_settings, 'email_enabled', false)
        }
      }
    },
    handleEmailChange() {
      if (this.user.business.social_fusion_settings.auto_sharing_settings.email_enabled) {
        this.$set(this.user.business.social_fusion_settings.auto_sharing_settings, 'fb_enabled', false)
        this.$set(this.user.business.social_fusion_settings.auto_sharing_settings, 'ig_enabled', false)
        this.$set(this.user.business.social_fusion_settings.auto_sharing_settings, 'gbp_enabled', false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.settings-content {
  margin-top: 24px;
}

// Tab Navigation
.tabs-container {
  width: 100%;
  margin-bottom: 32px;
}

.tabs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
  min-height: 40px;
  align-items: center;
  border-radius: 6px;
  background: #f3f4f6;
  padding: 4px;
}

.tab-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.2s;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    color: #1f2937;
  }

  &.active {
    background: #fff;
    color: #1f2937;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.tab-panels-wrapper {
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.connection-panel {
  order: -1;
  margin-bottom: 24px;
}

.tab-panel {
  padding: 0;
}

// Account Tab Content
.account-tab-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.account-profile-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.account-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 576px) {
  .account-form-row {
    grid-template-columns: 1fr;
  }
}

.account-form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.account-label {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.account-input {
  height: 40px;
  padding: 8px 12px;
  font-size: 14px;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s;
}

.account-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.account-form-actions {
  margin-top: 8px;
}

.account-password-section {
  padding-top: 0;
}

.account-password-section :deep(.card) {
  border: none;
  box-shadow: none;
  margin-bottom: 0;
}

.account-password-section :deep(.card-body) {
  padding: 0;
}

// Settings Tab Content
.settings-tab-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

// Settings Section
.settings-section {
  margin-bottom: 0;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
}

// Recipient Cards
.recipient-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
}

.recipient-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.recipient-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.recipient-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.recipient-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  background: #e8e8e8;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.add-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2563eb;
  }
}

.recipient-table-wrapper {
  overflow-x: auto;
}

.negative-feedback-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #374151;
}

.negative-feedback-hint {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.recipient-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    tr {
      background: #f9fafb;
    }
  }

  th {
    padding: 12px;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    border-bottom: 1px solid #e5e7eb;
  }

  td {
    padding: 12px;
    font-size: 14px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
  }

  tbody tr:hover {
    background: #f9fafb;
  }
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 8px;

  &.edit-btn {
    background: #e8e8e8;
    color: #1f2937;

    &:hover {
      background: #d1d5db;
    }
  }

  &.delete-btn {
    background: #dc2626;
    color: white;

    &:hover {
      background: #b91c1c;
    }
  }
}

// Connected Sites
.connected-sites-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.connection-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.connection-card-inner {
  padding: 24px;
}

.connection-icons-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.connection-icon-fb {
  width: 64px;
  height: 64px;
  background: #2563eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.connection-icon-ig {
  width: 64px;
  height: 64px;
  background: linear-gradient(to bottom right, #9333ea, #db2777);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 16px;
}

.connection-icon-google {
  width: 64px;
  height: 64px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.connection-icon-svg {
  width: 32px;
  height: 32px;
  color: white;
}

.connection-icon-google .connection-icon-svg {
  color: inherit;
}

.connection-content-wrapper {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.connection-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.btn-connected-full,
.btn-primary-full,
.btn-danger-full {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  text-decoration: none;
  width: 100%;
  height: 40px;
  padding: 8px 16px;
  border: none;
  outline: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}

.btn-connected-full {
  background: #f3f4f6;
  color: #1f2937;

  &:hover {
    background: #e5e7eb;
  }
}

.btn-primary-full {
  background: #3b82f6;
  color: white;

  &:hover {
    background: #2563eb;
  }
}

.btn-danger-full {
  background: #dc2626;
  color: white;

  &:hover {
    background: #b91c1c;
  }
}

.error-message-block {
  display: block;
  font-size: 12px;
  color: #dc2626;
  margin-top: 8px;
  text-align: left;
}

.connection-details {
  text-align: center;
  font-size: 14px;
  color: #6b7280;
}

.connection-detail-group {
  margin-top: 12px;
}

.connection-detail-label {
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 8px;
  margin-top: 0;
  text-align: center;
}

.connection-detail-text {
  margin: 4px 0;
  color: #1f2937;
  text-align: center;
}

.connection-select-full {
  display: flex;
  height: 40px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: white;
  padding: 8px 12px;
  font-size: 14px;
  color: #1f2937;
  cursor: pointer;
  outline: none;
  transition: all 0.2s;

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-color: #3b82f6;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

// AI Prompt Section
.ai-prompt-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
}

.ai-prompt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.btn-blue-save {
  background-color: #3b82f6 !important;
  border-color: #3b82f6 !important;
  color: white !important;

  &:hover:not(:disabled) {
    background-color: #2563eb !important;
    border-color: #2563eb !important;
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25) !important;
  }
}

// History Section
.history-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.history-card {
  width: 100%;
}

.history-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px;
}

.history-content {
  padding: 0 24px 24px 24px;
}

.history-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.025em;
  color: #1f2937;
  margin: 0;
}

.history-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.history-item-left {
  flex: 1;
}

.history-action {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.history-action-failed {
  color: #b91c1c;
}

.history-retry {
  margin-top: 8px;
  border: 1px solid #b91c1c;
  background: #fff;
  color: #b91c1c;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;
}

.history-retry:disabled {
  opacity: 0.6;
  cursor: default;
}

.history-detail {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.history-link {
  font-size: 13px;
  margin: 4px 0 0;
  word-break: break-all;

  a {
    color: rgb(36, 99, 235);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.history-time {
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
  margin-left: 16px;
}

.history-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.history-total {
  font-size: 14px;
  color: #6b7280;
}

.empty-history {
  text-align: center;
  padding: 48px;
  color: #6b7280;
}

// Automated Requests Section
.automated-requests-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.automated-requests-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 24px;
}

.automated-requests-content {
  padding: 0 24px 24px 24px;
}

.automated-requests-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.025em;
  color: #1f2937;
  margin: 0;
}

.automated-requests-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.automated-requests-tabs-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.automated-requests-tabs {
  display: inline-flex;
  height: 40px;
  align-items: center;
  justify-content: flex-start;
  border-radius: 6px;
  background: #f1f5f9;
  padding: 4px;
  color: #6b7280;
  width: fit-content;
}

.sub-tab-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &.active {
    background: white;
    color: #1f2937;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.sub-tab-panel {
  margin-top: 16px;
}

.webhook-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.webhook-section-item {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.webhook-section-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.webhook-url-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.method-badge-post {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: none;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  background: #dc2626;
  color: white;
  cursor: default;
  transition: all 0.2s;

  &:hover {
    background: #b91c1c;
  }
}

.webhook-url-code {
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-family: monospace;
  color: #1f2937;
}

.properties-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.property-item-new {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.property-badges-new {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.property-name-badge-new {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  background: transparent;
  color: #1f2937;
  cursor: default;
}

.property-type-badge-new {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: none;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  background: #f3f4f6;
  color: #1f2937;
  cursor: default;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }
}

.property-constraint-badge-new {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  border: none;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  background: #dc2626;
  color: white;
  cursor: default;
  transition: all 0.2s;

  &:hover {
    background: #b91c1c;
  }
}

.property-description-new {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.log-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.log-total {
  font-size: 14px;
  color: #6b7280;
}

.empty-log {
  text-align: center;
  padding: 48px;
  color: #6b7280;
}

// Google Button
.google-btn {
  width: 225px;
  height: 42px;
  background-color: #4285f4;
  border-radius: 2px;
  box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.25);
  position: relative;
  cursor: pointer;
  margin-bottom: 16px;

  .google-icon-wrapper {
    position: absolute;
    margin-top: 1px;
    margin-left: 1px;
    width: 40px;
    height: 40px;
    border-radius: 2px;
    background-color: #fff;
  }

  .google-icon {
    position: absolute;
    margin-top: 11px;
    margin-left: 11px;
    width: 18px;
    height: 18px;
  }

  .btn-text {
    float: right;
    margin: 11px 11px 0 0;
    color: #fff;
    font-size: 14px;
    letter-spacing: 0.2px;
    font-family: 'Roboto';
  }

  &:hover {
    box-shadow: 0 0 6px #4285f4;
  }

  &:active {
    background: #1669f2;
  }
}

.timezone-select {
  max-width: 400px;
}

.time-td {
  .b-form-spinbutton {
    button {
      color: #222;
    }
  }
}

.leaderboard-reports-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 8px;
  line-height: 1.5;
}

.leaderboard-reports-est {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 20px;
}

.leaderboard-reports-form {
  padding-top: 4px;
}

.leaderboard-reports-field {
  margin-bottom: 24px;
}

.leaderboard-reports-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.leaderboard-report-days {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}

.leaderboard-report-day-checkbox {
  margin: 0;
}

.leaderboard-reports-actions {
  margin-top: 8px;
}
</style>

<style lang="scss">
.location-connection-modal {
  .modal-dialog {
    max-width: 100%;
    margin: 0;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100vh;
    display: flex;
    position: fixed;
    z-index: 100000;
    border-radius: 0;

    .modal-header,
    .modal-content,
    .modal-footer {
      border-radius: 0;
      border: 0;
    }

    .modal-content {
      background: #edeff4;
    }

    select {
      background: #f3f8fb;
    }
  }
}
</style>